import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React, { ForwardedRef, forwardRef, MouseEvent, useState } from 'react';

import ActionDialog from '~components/ActionDialog';
import OberonCard from '~components/OberonCard';

type Prop = {
  title: string;
  subTitle?: string;
  id?: string;
  onEdit?: () => void;
  onDelete?: (skipArchive: boolean) => Promise<void>;
};

const ContactFlowsCard = forwardRef(
  ({ title, subTitle, id, onEdit, onDelete }: Prop, ref: ForwardedRef<HTMLDivElement>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState<boolean>(false);
    const [skipArchive, setSkipArchive] = useState<boolean>(false);
    const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const triggerEdit = () => {
      if (!onEdit) {
        console.error('triggerEdit: onEdit not defined');
        return;
      }

      setAnchorEl(null);
      onEdit();
    };

    const triggerDelete = async () => {
      if (!onDelete) {
        console.error('triggerDelete: onDelete not defined');
        return;
      }

      try {
        await onDelete(skipArchive);
        setDeleteConfirmModalOpen(false);
      } catch (e) {
        return;
      }
    };

    const handleDeleteConfirmationOpen = () => {
      setAnchorEl(null);
      setDeleteConfirmModalOpen(true);
    };

    const handleDeleteConfirmationClose = () => {
      setDeleteConfirmModalOpen(false);
      setSkipArchive(false);
    };

    return (
      <>
        <OberonCard
          ref={ref}
          titleFontWeight={700}
          title={title}
          subHeader={subTitle}
          action={
            <>
              <IconButton aria-controls={id} aria-haspopup={id ? true : undefined} onClick={handleMenuOpen}>
                <MoreHorizIcon />
              </IconButton>

              {(onEdit || onDelete) && (
                <Menu id={id} anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {onEdit && <MenuItem onClick={triggerEdit}>Edit</MenuItem>}
                  {onDelete && <MenuItem onClick={handleDeleteConfirmationOpen}>Delete</MenuItem>}
                </Menu>
              )}
            </>
          }
        />

        <ActionDialog
          open={deleteConfirmModalOpen}
          title='Are you sure you want to do this?'
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>{`You are about to delete contact flow ${title}.`}</Typography>
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={skipArchive} onChange={(e) => setSkipArchive(e.target.checked)} />}
                  label='Do not archive current version'
                />
                <Typography variant='caption' display='block' sx={{ pl: 4, color: 'red' }}>
                  This option will delete the contact flow without saving it to the version history.
                </Typography>
              </Box>
            </Box>
          }
          onClose={handleDeleteConfirmationClose}
          onAccept={triggerDelete}
          primaryActionTitle='Delete'
        />
      </>
    );
  },
);

export default ContactFlowsCard;
